import React from "react";

const ResourceCard = ({
  title,
  description,
  link,
  borderColor,
  icon,
  comingSoon,
}) => {
  return (
    <div
      className={`card p-6 flex flex-col items-center text-center border-t-4 ${borderColor} shadow-lg`}
    >
      <div className="w-12 h-12 text-blue-400 mb-4">{icon}</div>
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      <p className="mt-2 text-sm text-gray-400">{description}</p>

      {comingSoon ? (
        <div
          className="mt-4 inline-block px-4 py-2 bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300 rounded-md cursor-not-allowed relative group border border-gray-300 dark:border-gray-500"
          title="This resource is currently in development and will be available soon!"
        >
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Coming Soon
          </div>
          {/* Tooltip */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white dark:bg-gray-700 dark:text-gray-100 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
            This resource is in development
          </div>
        </div>
      ) : (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
        >
          View Resource &rarr;
        </a>
      )}
    </div>
  );
};

const ResourcesHub = () => {
  const resources = [
    {
      title: "Reusable Scripts",
      description:
        "Download and use our pre-built scripts for common automation tasks.",
      link: "https://github.com/test-omega/reusable-scripts",
      borderColor: "border-t-red-500",
      comingSoon: true,
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 13h6m-3-3v6m-9 1a2 2 0 012-2h10a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6z"
          />
        </svg>
      ),
    },
    {
      title: "Test Templates",
      description:
        "Start your new project with our professionally designed test templates.",
      link: "https://github.com/test-omega/test-templates",
      borderColor: "border-t-green-500",
      comingSoon: true,
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 6.253v13m0-13C10.832 5.484 9.494 5 8 5c-3.314 0-6 2.686-6 6s2.686 6 6 6c1.494 0 2.832-.484 4-1.253M12 6.253v13c1.168-.767 2.506-1.253 4-1.253 3.314 0 6-2.686 6-6s-2.686-6-6-6c-1.494 0-2.832.484-4 1.253"
          />
        </svg>
      ),
    },
    {
      title: "Open-Source Tools",
      description:
        "A curated list of the best open-source tools for test automation and QA.",
      link: "/open-source-tools",
      borderColor: "border-t-yellow-500",
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
    },
    {
      title: "Integrations",
      description:
        "Discover integrations with popular CI/CD pipelines, reporting tools, and more.",
      link: "https://github.com/test-omega/integrations",
      borderColor: "border-t-indigo-500",
      comingSoon: true,
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M8 9l3 3-3 3m5 0h1.5a2.5 2.5 0 002.5-2.5v-1a2.5 2.5 0 00-2.5-2.5H12a2.5 2.5 0 00-2.5 2.5v1a2.5 2.5 0 002.5 2.5h2.5z"
          />
        </svg>
      ),
    },
  ];

  return (
    <section id="resources" className="py-16 md:py-24">
      <h2 className="text-4xl font-bold text-center text-white mb-12">
        Resources Hub
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {resources.map((resource, index) => (
          <ResourceCard key={index} {...resource} />
        ))}
      </div>
    </section>
  );
};

export default ResourcesHub;
